<template>
  <div>
    <v-menu bottom rounded offset-y>
      <template v-slot:activator="{ on }">
        <v-btn color="grey" elevation="0" v-on="on" icon>
          <v-icon small>mdi-dots-vertical</v-icon>
        </v-btn>
      </template>
      <v-card>
        <v-list dense>
          <span v-for="(option, index) in options" :key="index">
            <v-list-item @click="emitModalAndAppointment(option.modal, option.fetchAppointment)" v-if="option.show"
              link>
              <v-icon class="mr-3 grey--text text--lighten-1" style="transform: scale(0.85); opacity: 0.75">
                {{ option.icon }}
              </v-icon>
              <span class="body-2">
                {{ option.title }}
              </span>
            </v-list-item>
          </span>
        </v-list>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { rentOutTypes } from '@/components/appointment/formFieldsCreate/appointmentFormDefaults'

export default {
  name: 'SelectedMarkersMenu',

  props: {
    group: {
      type: Object,
      required: false,
    },

    appointment: {
      type: Object,
      required: true,
    }
  },

  computed: {
    ...mapGetters(['serviceReport']),

    groupId() {
      return this.group?.id
    },

    isNotDone() {
      return this.appointment.status !== 'DONE'
    },

    isRentOut() {
      return rentOutTypes.includes(this.appointment?.type)
    },

    isAdminOrPartner() {
      const userRoles = localStorage.getItem('access_roles')
      if (!userRoles) return false

      const userRolesArray = userRoles?.split(',')
      return userRolesArray.includes('ROLE_ADMIN') || userRolesArray.includes('ROLE_PARTNER')
    },

    canAddHost() {
      return ['OPEN', 'DRAFT', 'REJECTED'].includes(this.appointment.status) && !this.appointment.host && !this.groupId
    },

    canChangeHost() {
      return ['SCHEDULED', 'REJECTED'].includes(this.appointment.status) && !this.groupId
    },

    isNotCanceled() {
      return this.appointment.status !== 'CANCELED'
    },

    hasVisitorOrTenant() {
      return this.appointment?.visitor_name !== null
    },

    canChangeAppointmentStatus() {
      return !this.groupId && this.isNotCanceled
    },

    canChangeAppointmentKeyLocation() {
      return this.appointment.status == 'OPEN' && !this.appointment.host
    },

    canChangeAppointmentType() {
      return this.appointment.status == 'OPEN' && !this.appointment.host
    },

    canChangeDate() {
      return ['SCHEDULED', 'OPEN'].includes(this.appointment.status);
    },

    canReopenQuote() {
      return this.isRentOut && ['SCHEDULED', 'DONE'].includes(this.appointment.status)
        && !this.isQuoteAlreadyPaid && this.isServiceReportFinished && !this.isHostAlreadyPaid
    },

    isHostAlreadyPaid() {
      return this.appointment?.payments?.filter(payment => payment.status).length > 0
    },

    isQuoteAlreadyPaid() {
      return this.serviceReport?.quotes
        ? ['RECEIVED', 'CONFIRMED'].includes(this.serviceReport?.quotes[0]?.charge?.installments[0]?.status)
        : false
    },

    appointmentId() {
      return this.appointment?.id
    },

    options() {
      return [
        {
          show: this.groupId,
          modal: 'REMOVE_APPOINTMENT_FROM_GROUP_MODAL',
          icon: 'mdi-trash-can-outline',
          title: this.$t('appointment.adminActions.removeFromGroup'),
        },
        {
          show: true,
          fetchAppointment: true,
          modal: 'RESCHEDULE_APPOINTMENT_MODAL',
          icon: 'mdi-calendar-refresh-outline',
          title: this.$t('appointment.adminActions.reschedule'),
        },
        {
          show: this.isNotCanceled && this.isNotDone && !this.groupId,
          fetchAppointment: true,
          modal: 'CANCEL_APPOINTMENT_MODAL',
          icon: 'mdi-calendar-remove-outline',
          title: this.$t('board.actions.cancel'),
        },
        {
          show: true,
          fetchAppointment: true,
          modal: 'ADDITIONAL_INFO_APPOINTMENT_MODAL',
          icon: 'mdi-information-box-outline',
          title: this.$t('appointment.adminActions.additionalInfo'),
        },
        {
          show: this.isAdminOrPartner,
          fetchAppointment: true,
          modal: 'BULK_SEND_CONFIRMATION_EMAIL_MODAL',
          icon: 'mdi-email-check-outline',
          title: this.$t('mapDrawing.sendConfirmationEmail'),
        },
        {
          show: this.isAdminOrPartner,
          fetchAppointment: true,
          modal: 'CONFIRM_APPOINTMENT_MODAL',
          icon: 'mdi-checkbox-marked-circle-outline',
          title: this.$t('appointment.adminActions.confirmAppointmentManually'),
        },
        {
          show: true,
          fetchAppointment: true,
          modal: 'UPDATE_OBSERVATIONS_APPOINTMENT_MODAL',
          icon: 'mdi-text-box-edit-outline',
          title: this.$t('appointment.adminActions.updateObservations.action'),
        },
        {
          show: this.canAddHost,
          fetchAppointment: true,
          modal: 'ADD_HOST_MODAL',
          icon: 'mdi-account-plus',
          title: this.$t('appointment.adminActions.addHost.action'),
        },
        {
          show: this.canChangeHost,
          fetchAppointment: true,
          modal: 'CHANGE_APPOINTMENT_HOST_MODAL',
          icon: 'mdi-account-sync-outline',
          title: this.$t('appointment.adminActions.changeHost.action'),
        },
        {
          show: this.hasVisitorOrTenant && this.isNotCanceled,
          fetchAppointment: true,
          modal: 'EDIT_VISITOR_MODAL',
          icon: 'mdi-account-details-outline',
          title: this.isVisitType ? this.$t('board.actions.editVisitor') : this.$t('board.actions.editTenant'),
        },
        {
          show: this.canChangeAppointmentStatus,
          fetchAppointment: true,
          modal: 'CHANGE_APPOINTMENT_STATUS_MODAL',
          icon: 'mdi-flag-outline',
          title: this.$t('appointment.adminActions.changeStatus.action'),
        },
        {
          show: this.canChangeAppointmentKeyLocation && this.isNotCanceled,
          fetchAppointment: true,
          modal: 'CHANGE_APPOINTMENT_KEY_LOCATION_MODAL',
          icon: 'mdi-key-outline',
          title: this.$t('appointment.adminActions.changeKeyLocation.action'),
        },
        {
          show: this.canChangeAppointmentType && this.isNotCanceled,
          fetchAppointment: true,
          modal: 'CHANGE_APPOINTMENT_TYPE_MODAL',
          icon: 'mdi-briefcase-arrow-left-right-outline',
          title: this.$t('appointment.adminActions.changeType.action'),
        },
        {
          show: this.isAdminOrPartner,
          fetchAppointment: false,
          modal: () => window.open('../confirm-appointment/' + this.appointmentId + '?isChangeDateAllowed=true', '_blank'),
          icon: 'mdi-file-document-check-outline',
          title: this.$t('appointment.adminActions.showConfirmationPage'),
        },
      ]
    },
  },

  methods: {
    ...mapMutations(['setIsAppointmentLoading']),
    ...mapActions(['fetchAppointmentAsAdmin', 'fetchAppointment']),

    refreshMap() {
      this.$emit('refreshMap')
    },

    async emitModalAndAppointment(modal, fetchAppointment, confirmModal = false) {
      if (typeof modal === 'function') {
        modal()
        return
      }

      this.$emit('modalAndAppointment', {
        modal,
        confirmModal,
        appointment: this.appointment,
      })

      if (!fetchAppointment) return
      await this.fetchAppointmentWithLoading()
    },

    async fetchAppointmentWithLoading() {
      this.setIsAppointmentLoading(true)

      try {
        const fetcher = this.isAdminOrPartner
          ? this.fetchAppointmentAsAdmin
          : this.fetchAppointment

        await fetcher(this.appointment.id)
      } finally {
        this.setIsAppointmentLoading(false)
      }
    },
  },
}
</script>
